import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import { 
  Utensils, Droplet, Receipt, Hand, UtensilsCrossed, 
  Clock, CheckCircle, Loader, QrCode, Download
} from 'lucide-react';
import DigiHostessSetup from './DigiHostessSetup';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DigiHostess = () => {
  // Helper to get auth token (supports both user and admin)
  const getAuthToken = () => localStorage.getItem('auth_token') || localStorage.getItem('admin_token');
  
  const [state, setState] = useState({
    tables: [],
    waiters: [],
    requests: [],
    orders: []
  });
  
  const [selectedTable, setSelectedTable] = useState('');
  const [orderNotes, setOrderNotes] = useState('');
  const [isSetup, setIsSetup] = useState(false);
  const [showSetup, setShowSetup] = useState(false);
  const [showQRModal, setShowQRModal] = useState(false);
  const [selectedTableQR, setSelectedTableQR] = useState(null);
  const wsRef = useRef(null);

  useEffect(() => {
    loadState();
    connectWebSocket();
    
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, []);

  const connectWebSocket = () => {
    const wsUrl = API.replace('http', 'ws') + '/digihostess/ws';
    const ws = new WebSocket(wsUrl);
    
    ws.onopen = () => {
      console.log('WebSocket connected');
    };
    
    ws.onmessage = (event) => {
      const msg = JSON.parse(event.data);
      
      if (msg.type === 'NEW_REQUEST') {
        setState(prev => ({
          ...prev,
          requests: [msg.data, ...prev.requests]
        }));
        toast.info(`New request from Table ${msg.data.table}: ${msg.data.type}`);
      }
      
      if (msg.type === 'NEW_ORDER') {
        setState(prev => ({
          ...prev,
          orders: [msg.data, ...prev.orders]
        }));
        toast.info(`New order from Table ${msg.data.table}`);
      }
      
      if (msg.type === 'REQUEST_UPDATED') {
        setState(prev => ({
          ...prev,
          requests: prev.requests.map(r => 
            r.id === msg.data.id ? msg.data : r
          )
        }));
      }
    };
    
    ws.onerror = (error) => {
      console.error('WebSocket error:', error);
    };
    
    ws.onclose = () => {
      console.log('WebSocket disconnected, reconnecting...');
      setTimeout(connectWebSocket, 3000);
    };
    
    wsRef.current = ws;
  };

  const loadState = async () => {
    try {
      const response = await axios.get(`${API}/digihostess/state`, {
        headers: { Authorization: `Bearer ${getAuthToken()}` }
      });
      
      setState(response.data);
      setIsSetup(response.data.tables.length > 0);
      
      if (response.data.tables.length > 0) {
        setSelectedTable(response.data.tables[0].number);
      }
    } catch (error) {
      console.error('Failed to load state:', error);
    }
  };

  const setupRestaurant = async () => {
    try {
      await axios.post(`${API}/digihostess/setup`, {
        tables: ['101', '102', '103', '104', '105', '106', '107', '108'],
        waiters: [
          { id: 'w1', name: 'Waiter A' },
          { id: 'w2', name: 'Waiter B' },
          { id: 'w3', name: 'Waiter C' }
        ]
      }, {
        headers: { Authorization: `Bearer ${getAuthToken()}` }
      });
      
      toast.success('Restaurant setup complete!');
      loadState();
    } catch (error) {
      toast.error('Setup failed');
      console.error('Setup error:', error);
    }
  };

  const sendRequest = async (type) => {
    if (!selectedTable) {
      toast.error('Please select a table');
      return;
    }
    
    try {
      await axios.post(`${API}/digihostess/request`, {
        table: selectedTable,
        type
      }, {
        headers: { Authorization: `Bearer ${getAuthToken()}` }
      });
      
      toast.success(`${type} request sent from Table ${selectedTable}`);
    } catch (error) {
      toast.error('Failed to send request');
    }
  };

  const sendOrderNotes = async () => {
    if (!selectedTable || !orderNotes.trim()) {
      toast.error('Please select a table and enter order notes');
      return;
    }
    
    try {
      await axios.post(`${API}/digihostess/order`, {
        table: selectedTable,
        text: orderNotes
      }, {
        headers: { Authorization: `Bearer ${getAuthToken()}` }
      });
      
      setOrderNotes('');
      toast.success(`Order notes sent from Table ${selectedTable}`);
    } catch (error) {
      toast.error('Failed to send order');
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await axios.post(`${API}/digihostess/status`, {
        id,
        status
      }, {
        headers: { Authorization: `Bearer ${getAuthToken()}` }
      });
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return <Clock className="w-4 h-4 text-yellow-500" />;
      case 'in_progress':
        return <Loader className="w-4 h-4 text-blue-500 animate-spin" />;
      case 'done':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      default:
        return null;
    }
  };

  const getRequestIcon = (type) => {
    switch (type) {
      case 'bread':
        return <Utensils className="w-5 h-5" />;
      case 'water':
        return <Droplet className="w-5 h-5" />;
      case 'bill':
        return <Receipt className="w-5 h-5" />;
      case 'call':
        return <Hand className="w-5 h-5" />;
      case 'cutlery':
        return <UtensilsCrossed className="w-5 h-5" />;
      default:
        return <Hand className="w-5 h-5" />;
    }
  };

  const viewTableQR = async (tableId) => {
    try {
      const response = await axios.get(`${API}/digihostess/table/${tableId}/qr`, {
        headers: { Authorization: `Bearer ${getAuthToken()}` }
      });
      setSelectedTableQR(response.data);
      setShowQRModal(true);
    } catch (error) {
      toast.error('Failed to generate QR code');
    }
  };

  const downloadQR = () => {
    if (!selectedTableQR) return;
    
    const link = document.createElement('a');
    link.href = selectedTableQR.qr_code;
    link.download = `table-${selectedTableQR.table.number}-qr.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (!isSetup || showSetup) {
    return <DigiHostessSetup onComplete={() => {
      setIsSetup(true);
      setShowSetup(false);
      loadState();
    }} />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">DigiHostess</h1>
            <p className="text-slate-400">Real-time service orchestration for your restaurant</p>
          </div>
          <Button
            onClick={() => setShowSetup(true)}
            variant="outline"
            className="flex items-center gap-2"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z"/><circle cx="12" cy="12" r="3"/></svg>
            Manage Setup
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Guest Panel */}
          <Card className="p-6 bg-slate-800 border-slate-700">
            <h2 className="text-xl font-bold text-white mb-4">Guest Panel (Table QR)</h2>
            
            <select 
              value={selectedTable}
              onChange={(e) => setSelectedTable(e.target.value)}
              className="w-full p-3 rounded-lg bg-slate-700 text-white border-slate-600 mb-4"
            >
              {state.tables.map(t => (
                <option key={t.number} value={t.number}>Table {t.number}</option>
              ))}
            </select>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-6">
              <Button 
                onClick={() => sendRequest('bread')}
                className="flex items-center gap-2 bg-amber-600 hover:bg-amber-700"
              >
                <Utensils className="w-4 h-4" />
                Bread
              </Button>
              <Button 
                onClick={() => sendRequest('water')}
                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700"
              >
                <Droplet className="w-4 h-4" />
                Water
              </Button>
              <Button 
                onClick={() => sendRequest('bill')}
                className="flex items-center gap-2 bg-green-600 hover:bg-green-700"
              >
                <Receipt className="w-4 h-4" />
                Bill
              </Button>
              <Button 
                onClick={() => sendRequest('call')}
                className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700"
              >
                <Hand className="w-4 h-4" />
                Call Server
              </Button>
              <Button 
                onClick={() => sendRequest('cutlery')}
                className="flex items-center gap-2 bg-slate-600 hover:bg-slate-700"
              >
                <UtensilsCrossed className="w-4 h-4" />
                Cutlery
              </Button>
            </div>

            <div className="border-t border-slate-700 pt-4">
              <h3 className="text-white font-semibold mb-2">Order Notes (Draft)</h3>
              <textarea
                value={orderNotes}
                onChange={(e) => setOrderNotes(e.target.value)}
                placeholder="Type order notes here..."
                rows="4"
                className="w-full p-3 rounded-lg bg-slate-700 text-white border-slate-600 mb-3"
              />
              <Button 
                onClick={sendOrderNotes}
                className="w-full bg-blue-600 hover:bg-blue-700"
              >
                Send Order Notes
              </Button>
            </div>
          </Card>

          {/* Requests Panel */}
          <Card className="p-6 bg-slate-800 border-slate-700">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-white">Floor Requests</h2>
              <Button
                onClick={() => setShowQRModal(true)}
                size="sm"
                variant="outline"
                className="flex items-center gap-2"
              >
                <QrCode className="w-4 h-4" />
                View QR Codes
              </Button>
            </div>
            
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {state.requests.length === 0 ? (
                <p className="text-slate-400 text-center py-8">No requests yet</p>
              ) : (
                state.requests.map(r => (
                  <div 
                    key={r.id}
                    className="p-4 bg-slate-700 rounded-lg border border-slate-600"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        {getRequestIcon(r.type)}
                        <span className="font-semibold text-white">Table {r.table}</span>
                        <span className="text-slate-400">– {r.type.toUpperCase()}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        {getStatusIcon(r.status)}
                        <span className="text-xs text-slate-400 capitalize">{r.status}</span>
                      </div>
                    </div>
                    
                    {r.status !== 'done' && (
                      <div className="flex gap-2">
                        {r.status === 'pending' && (
                          <Button 
                            onClick={() => updateStatus(r.id, 'in_progress')}
                            size="sm"
                            className="bg-blue-600 hover:bg-blue-700"
                          >
                            Start
                          </Button>
                        )}
                        <Button 
                          onClick={() => updateStatus(r.id, 'done')}
                          size="sm"
                          className="bg-green-600 hover:bg-green-700"
                        >
                          Done
                        </Button>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </Card>

          {/* Orders Panel */}
          <Card className="p-6 bg-slate-800 border-slate-700 lg:col-span-2">
            <h2 className="text-xl font-bold text-white mb-4">Kitchen / Order Notes Feed</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-96 overflow-y-auto">
              {state.orders.length === 0 ? (
                <p className="text-slate-400 text-center py-8 col-span-2">No orders yet</p>
              ) : (
                state.orders.map(o => (
                  <div 
                    key={o.id}
                    className="p-4 bg-slate-700 rounded-lg border border-slate-600"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-semibold text-white">Table {o.table}</span>
                      <span className="text-xs text-slate-400">
                        {new Date(o.created_at).toLocaleTimeString()}
                      </span>
                    </div>
                    <p className="text-slate-300 whitespace-pre-wrap mb-2">{o.text}</p>
                    <p className="text-xs text-slate-500">
                      Draft note – re-enter in POS for billing
                    </p>
                  </div>
                ))
              )}
            </div>
          </Card>
        </div>

        {/* QR Code Modal */}
        {showQRModal && (
          <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
            <Card className="max-w-4xl w-full max-h-[90vh] overflow-y-auto bg-slate-800 border-slate-700 p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-white">Table QR Codes</h2>
                <Button
                  onClick={() => {
                    setShowQRModal(false);
                    setSelectedTableQR(null);
                  }}
                  variant="ghost"
                  size="sm"
                >
                  ✕
                </Button>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {state.tables.map(table => (
                  <div
                    key={table.id}
                    className="p-4 bg-slate-700 rounded-lg border border-slate-600 text-center cursor-pointer hover:bg-slate-600"
                    onClick={() => viewTableQR(table.id)}
                  >
                    <QrCode className="w-8 h-8 mx-auto mb-2 text-slate-400" />
                    <p className="text-white font-semibold">Table {table.number}</p>
                  </div>
                ))}
              </div>
              
              {selectedTableQR && (
                <div className="mt-6 p-6 bg-white rounded-lg">
                  <div className="text-center">
                    <h3 className="text-2xl font-bold text-slate-900 mb-4">
                      Table {selectedTableQR.table.number}
                    </h3>
                    <img
                      src={selectedTableQR.qr_code}
                      alt={`QR Code for Table ${selectedTableQR.table.number}`}
                      className="mx-auto mb-4"
                      style={{ width: '300px', height: '300px' }}
                    />
                    <Button
                      onClick={downloadQR}
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Download QR Code
                    </Button>
                  </div>
                </div>
              )}
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default DigiHostess;
